import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import TopMenu from '../TopMenu';
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../../services/notificationService';
import { formatTimeAgo, groupByDay } from '../../utils/timeAgo';
import { notificationConfig } from './NotificationBell';
import './NotificationsPage.css';

const getConfig = (type) => notificationConfig[type] || notificationConfig.system;

const NotificationsPage = ({ theme = 'dark', toggleTheme }) => {
  const navigate = useNavigate();
  const dark = theme === 'dark';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getNotifications();
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems((arr) => arr.map((n) => ({ ...n, isRead: true })));
    } catch {}
  };

  const handleClickItem = async (n) => {
    try {
      if (!n.isRead) {
        await markNotificationRead(n.id);
        setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
      }
    } catch {}
    if (n.actionUrl) navigate(n.actionUrl);
  };

  const groups = items.reduce((acc, n) => {
    const key = groupByDay(n.createdAt);
    (acc[key] = acc[key] || []).push(n);
    return acc;
  }, {});
  const order = ['Today', 'Yesterday', 'Earlier'];

  return (
    <div className={`np-page ${dark ? 'np-dark' : 'np-light'}`}>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />
      <div className="np-container">
        <div className="np-head">
          <div>
            <h1 className="np-title">Notifications</h1>
            <p className="np-sub">Stay on top of your interviews and feedback.</p>
          </div>
          {items.some((n) => !n.isRead) && (
            <button className="np-mark-all" onClick={handleMarkAll}>
              <CheckCheck size={14} /> Mark all read
            </button>
          )}
        </div>

        {loading && <div className="np-loading">Loading…</div>}

        {!loading && items.length === 0 && (
          <div className="np-empty">
            <div className="np-empty-icon"><Bell size={42} /></div>
            <h3>No notifications yet</h3>
            <p>You'll see interview, feedback, and subscription updates here.</p>
          </div>
        )}

        {!loading && order.map((g) => groups[g] && (
          <section key={g} className="np-group">
            <h2 className="np-group-title">{g}</h2>
            <div className="np-list">
              {groups[g].map((n) => {
                const cfg = getConfig(n.type);
                const I = cfg.Icon;
                return (
                  <div
                    key={n.id}
                    className={`np-item ${n.isRead ? '' : 'np-item-unread'}`}
                    onClick={() => handleClickItem(n)}
                  >
                    <div className="np-ic" style={{ background: cfg.bg, color: cfg.color }}>
                      <I size={18} />
                    </div>
                    <div className="np-body">
                      <div className="np-row">
                        <h4>{n.title}</h4>
                        <span className="np-time">{formatTimeAgo(n.createdAt)}</span>
                      </div>
                      <p>{n.message}</p>
                    </div>
                    {!n.isRead && <span className="np-dot" />}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
};

export default NotificationsPage;
